import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, catchError, firstValueFrom, forkJoin, map, of, share, switchMap, tap, throwError } from 'rxjs';
import { OrderEstimateRequest } from '@core/models/order';
import { OrderStage, OrderStageCounts, stagesToStatusParam } from '@core/models/order-stage';
import { mapOrderListItem } from '@core/state/order/order.selectors';
import { ActionItemResponse } from '@core/models/action-item';
import { ApiService } from '@core/services/api/api.service';
import { HelperService } from '@core/services/api/helper.service';
import { ErrorsService } from '@core/services/errors/errors.service';
import { DataLayerService } from '@core/services/seo/data-layer.service';
import { SessionService } from '@core/services/session/session.service';
import { DesignMockup, PositionArtwork, StorageService } from '@core/services/storage/storage.service';
import * as OrderActions from '@core/state/order/order.actions';
import { environment } from 'environments/environment';
import { AuthService } from '@auth0/auth0-angular';

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  protected apiRoot: string;
  protected apiMicroservice: string;

  constructor(
    protected apiService: ApiService,
    private helperService: HelperService,
    private store: Store,
    protected http: HttpClient,
    private dataLayerService: DataLayerService,
    private router: Router,
    private session: SessionService,
    private authService: AuthService,
    private errorsService: ErrorsService,
    private storage: StorageService
  ) {
    this.apiRoot = environment.apiRoot;
    this.apiMicroservice = environment.apiMicroservice;
   }

  /**
   * UPS tracking details for a shipment (Story 6.2 — feeds the Live Transit timeline).
   * The microservice takes an object query param `trackingQuery` with a `tracking`
   * property. Response shape is normalized/consumed in the Shipping feature; see
   * docs/ups-tracking-details-spec.md.
   */
  public getTrackingDetails(tracking: string, carrier?: string): Observable<any> {
    const url = `${this.apiMicroservice}ups/tracking-details`;
    // `carrier` hint per the microservice spec (docs/ups-tracking-details-spec.md,
    // "Enhancements requested") — lets the backend route non-UPS numbers (e.g.
    // 3rd-party FedEx shipments) without inferring the carrier itself. Ignored
    // until the multi-carrier support lands; UPS behavior is unchanged.
    let params = new HttpParams().set('tracking', tracking);
    if (carrier) { params = params.set('carrier', carrier); }
    return this.http.get<any>(url, { params }).pipe(share());
  }

  /**
   * Per-stage order counts for the Orders-home pipeline (CS-3428). Legacy endpoint
   * returns a flat map of stage token → count plus a `total`
   * (e.g. `{ under_review: 15, production: 40, ..., total: 66 }`). Note the backend
   * reports `production` where our OrderStage model uses `in_production` — callers
   * bridge the difference via `stageCountKey()`.
   */
  public getStageCounts(clientId: number | string, ownedByUserId?: number | string): Observable<OrderStageCounts> {
    const owned = ownedByUserId != null ? `?ownedByUserId=${ownedByUserId}` : '';
    const url = `${this.apiMicroservice}legacy/cp/orders/${clientId}/stage-counts${owned}`;
    return this.http.get<OrderStageCounts>(url).pipe(share());
  }

  /**
   * A single page (default 3) of orders in one pipeline stage, mapped to the same
   * row shape the orders table uses (`mapOrderListItem`). Powers the Orders-home
   * pipeline's per-stage preview — we only ever fetch the selected stage, never all
   * stages at once. `stage` is the OrderStage token, sent as the backend `status`
   * filter.
   */
  public getStageOrders(clientId: number | string, stage: OrderStage, limit = 3, ownedByUserId?: number | string): Observable<any[]> {
    return this.getAllOrders(clientId, 'all', 1, limit, '', stagesToStatusParam([stage]), ownedByUserId).pipe(
      map((res: any) => (res?.data ?? []).map(mapOrderListItem))
    );
  }

  public getAllOrders(client_id, type, page, limit, search, statusFilter?: string, ownedByUserId?: number | string, shippedWithinDays?: number) :Observable<Object> {
    const validateSearch = search ? search.trim() : '';
    let url = `${this.apiMicroservice}legacy/v2/cp/orders/${client_id}/${type}/${page}/${limit}`;

    // Query params: search (encoded) + status (comma-separated lowercase snake_case
    // stage tokens, left unencoded so commas stay literal, e.g.
    // ?status=art_department,in_production,ready_to_ship). Must stay on the v2
    // route: v1 ignores the multi-status filter and lacks the distinct-id
    // pagination fix. `ownedByUserId` scopes to orders whose main contact is that
    // portal user (docs/owned-orders-backend-spec.md). `shippedWithinDays`
    // restricts to orders with a non-void shipment shipped inside the window —
    // the Shipping page's "recently shipped" slice (BF-67). No `lookback` — the
    // Orders page fetches full history (the 30-day window was dropped, BF-55).
    const params: string[] = [];
    if (validateSearch) { params.push(`search=${encodeURIComponent(validateSearch)}`); }
    if (statusFilter) { params.push(`status=${statusFilter}`); }
    if (ownedByUserId != null) { params.push(`ownedByUserId=${ownedByUserId}`); }
    if (shippedWithinDays != null) { params.push(`shippedWithinDays=${shippedWithinDays}`); }
    if (params.length) { url += `?${params.join('&')}`; }

    // Perform the GET request
    return this.http.get(url).pipe(
      map((res: any) => this.rewriteOrderImageHosts(res)),
      // Proof thumbnails: the backend builds `mockup_img` as a legacy EC2
      // /uploads/ URL, but those files now live in S3, so the URL 404s and the
      // Proof cell renders broken. Swap in a presigned S3 URL where S3 has the
      // object (fails soft to the backend's URL otherwise). Resolution has to
      // complete before we emit, because `mapOrderListItem` copies rows into
      // fresh objects downstream. Ported from cs-legacy storageService.
      switchMap((res: any) => this.storage
        .resolveOrderThumbnails(Array.isArray(res?.data) ? res.data : [])
        .pipe(map(() => res))
      ),
      share()
    );
  }

  /**
   * Rewrite design position image_1..4 URLs to the hosted uploads location
   * (environment.uploadsUrl) so they resolve regardless of the API host the
   * backend returns (e.g. 127.0.0.1 in dev).
   */
  private rewriteOrderImageHosts(res: any): any {
    const orders = this.ordersFromResponse(res);
    if (Array.isArray(orders)) {
      for (const order of orders) {
        const designs = order?.designs ?? order?.design ?? [];
        for (const design of designs) {
          for (const pos of (design?.positions ?? [])) {
            if (!pos) { continue; }
            pos.image_1 = this.toUploadsHost(pos.image_1);
            pos.image_2 = this.toUploadsHost(pos.image_2);
            pos.image_3 = this.toUploadsHost(pos.image_3);
            pos.image_4 = this.toUploadsHost(pos.image_4);
          }
        }
      }
    }
    return res;
  }

  /** Orders carried by a response: handles both the list ({ data: [orders] })
   *  and the detail ({ data: order }), and tolerates a bare array or null data. */
  private ordersFromResponse(res: any): any[] {
    const data = Array.isArray(res) ? res : res?.data;
    return Array.isArray(data) ? data : (data ? [data] : []);
  }

  /**
   * Resolve design-position artwork (`image_1..4`) to presigned S3 URLs, then
   * emit the response unchanged. Authenticated detail route only — see the note
   * in `getPublicOrderDetail` for why the guest route opts out. Fails soft — on
   * any error the backend's original URLs survive.
   */
  private resolvePositionArtwork(res: any): Observable<any> {
    return this.storage
      .resolvePositionImages(this.ordersFromResponse(res))
      .pipe(map(() => res));
  }

  /** Swap the host of an /uploads/ URL to the hosted location; leave empty/non-uploads values untouched. */
  private toUploadsHost(url: any): any {
    if (typeof url !== 'string' || !url) { return url; }
    const marker = '/uploads/';
    const idx = url.indexOf(marker);
    if (idx === -1) { return url; }
    return environment.uploadsUrl + url.slice(idx + marker.length);
  }

  /**
   * Action Items (CS-3428) — tasks needing client attention, shown beside the
   * Order pipeline on the Orders home. `companyId` is required by the endpoint
   * (the decorator/company); `clientId` scopes the list to the active client
   * via the item's order (backend joins orders and filters o.client_id).
   * Legacy microservice endpoint; auth headers are attached by AuthInterceptor
   * for apiMicroservice URLs.
   */
  public getActionItems(companyId: number | string, clientId?: number | string, ownedByUserId?: number | string): Observable<ActionItemResponse[]> {
    const client = clientId != null ? `&clientId=${clientId}` : '';
    const owned = ownedByUserId != null ? `&ownedByUserId=${ownedByUserId}` : '';
    const url = `${this.apiMicroservice}legacy/action-items?companyId=${companyId}${client}${owned}`;
    return this.http.get<any>(url).pipe(
      // Tolerate both a bare array and a { data: [...] } envelope.
      map((res: any) => (Array.isArray(res) ? res : res?.data ?? [])),
      share()
    );
  }

  // get order headers based on client_id
  public getAllOrderHeaders(client_id) :Observable<Object> {
    let orders$ = this.apiService.getV2('api/v2/order/platform/headers/client/'+client_id).pipe(share())

    return orders$;
  }

  // get order headers affiliare based on client_id
  public getAllOrderAffiliateHeaders(user_id, decorator_id) :Observable<Object> {
    let orders$ = this.apiService.getV2('api/v2/order/platform/headers/affiliate/'+ user_id + '/' + decorator_id).pipe(share())

    return orders$;
  }

  public getAllAffiliateOrders(userId, decoratorId, type): Observable<Object> {
      let orders$ = this.apiService.getV2('api/v2/order/platform/'+userId + '/'+ decoratorId + '/' + type).pipe(share())

      return orders$;
  }

  getOrderDetail(orderId): Observable<Object> {
      const url = `${this.apiMicroservice}legacy/v2/order-details?order_id=${orderId}`;
      let orderDetail$ = this.http.get(url).pipe(
        map((res: any) => {
          // V2 returns an empty object {} for non-existent orders (HTTP 200).
          if (!res || !res.id) {
            this.errorsService.setErrorState({
              imageUrl: 'assets/images/oops-t-shirt.png',
              title: 'Order not found',
              message: `This order: <strong>${orderId}</strong> could not be found.`,
              buttons: [
                { routerLink: '/orders', text: 'Back to Orders' }
              ]
            });
            this.errorsService.showError();
            return { data: null };
          }
          return { data: res };
        }),
        map((res: any) => this.rewriteOrderImageHosts(res)),
        // Position artwork: same S3 migration as the list's Proof thumbnail —
        // `image_1..4` point at the legacy EC2 /uploads/ host, which now 404s.
        // Must run after the host rewrite above (it normalizes the URL into the
        // shape the key parser expects) and before we emit, since the detail
        // subscription is `take(1)`.
        switchMap((res: any) => this.resolvePositionArtwork(res)),
        catchError(async (error) => {
          if(error.status == 401){
            const client = this.session.getActiveClient();
            const authenticated = await firstValueFrom(this.authService.isAuthenticated$)
            if (authenticated) {
              this.errorsService.setErrorState({
                  imageUrl: 'assets/images/oops-t-shirt.png',
                  title: 'Order not found',
                  message: `This order: <strong>${orderId}</strong> is not available with your current client: <strong>${client.name}</strong>.`,
                  buttons: [
                    {
                    routerLink: `/login/choose-client`,
                    routerParams: {redirect: `/orders/${orderId}`},
                    text: 'Change Client'
                    }
                  ]
                })
                this.errorsService.showError()
            } else {
              this.router.navigate(['/login']);
            }
          }
          return of(error);
        }),
      )

      return orderDetail$
  }

  /**
   * Public, token-gated order detail for the guest "live link" view (Story 7.1).
   * `GET legacy/order/cp/:token` (api-microservices gateway) returns the same
   * `{ data, success, message }` envelope as the authenticated `getOrderDetail`,
   * so it flows through the same `getOrderDetailSuccess` reducer. The token is
   * the only credential — no Auth0 session or active client; the URL is in
   * AuthInterceptor's public allowlist. On an invalid/expired/revoked token the
   * backend returns 404/410; we surface the shared "Link unavailable" overlay.
   */
  getPublicOrderDetail(token: string): Observable<Object> {
    return this.http.get(`${this.apiMicroservice}legacy/order/cp/${encodeURIComponent(token)}`).pipe(
      map((res: any) => this.rewriteOrderImageHosts(res)),
      // Position artwork IS now presigned on this guest route (CS-4493), where it
      // previously was not. Both original blockers are gone: presign is in
      // AuthInterceptor's optional-auth list, so a token-less caller reaches it, and
      // guests now short-circuit past Auth0 entirely rather than stalling in its
      // token flow. Without this the card renders the legacy /uploads/ URL, which
      // 404s since the files moved to S3 — i.e. broken images for every guest.
      // Still fails soft (and capped by the resolver's own timeout), so a presign
      // outage costs a little first-paint latency, not a broken page.
      switchMap((res: any) => this.resolvePositionArtwork(res)),
      catchError((error) => {
        this.errorsService.setErrorState({
          imageUrl: 'assets/images/oops-t-shirt.png',
          title: 'Link unavailable',
          message: 'This order link is invalid or has expired. Please request a new link from your account manager.',
          buttons: [{ routerLink: '/login', text: 'Go to sign in' }]
        });
        this.errorsService.showError();
        return of(error);
      })
    );
  }

  /**
   * Mint a guest "live link" for an order (Story 7.1, Endpoint 3 — authenticated).
   * `POST legacy/order-links` (api-microservices gateway) returns `{ token, url,
   * expiresAt }`; the caller builds the shareable `/order/:token` URL from the
   * token. AuthInterceptor attaches the Auth0 token for this apiMicroservice
   * route (it is not in the public allowlist).
   */
  mintOrderLink(orderId: number, companyId: number, type: string = 'order'): Observable<{ token: string; url: string; expiresAt: string }> {
    return this.http.post<{ token: string; url: string; expiresAt: string }>(`${this.apiMicroservice}legacy/order-links`, { orderId, companyId, type });
  }

  public getOrderDetailV2(displayNumber, companyId): Observable<Object> {
    return this.apiService.getV2(`api/v2/order/client-portal/approvals/${displayNumber}/${companyId}`).pipe(share())
  }

  public createOrderEstimate(order: OrderEstimateRequest, files: any): Observable<Object>{
    let estimate$ = this.apiService.postV2('api/v2/order/platform/request-estimate', order).pipe(
      tap({
        next: (result) => {
          let orderId = result['id']
          this.dataLayerService.logOrderConversionEvent("OrderEstimateSubmit", "'Buttons'", "'Clicked'", "OrderEstimateSubmitButton", {orderId: orderId, data: order})
          this.store.dispatch(OrderActions.uploadFilesNewOrderEstimate({fdata: files, orderId}))
        },
        error: (error) => console.warn('create order estimate', error)
      }
      )
    );

    return estimate$;

  }

  public uploadFilesOrderEstimate(files, orderId): Observable<Object>{
    let upload = this.formatFileUpload(files, orderId);
    let upload$ = this.http.post(`${this.apiRoot}upload/create-multi`, upload, {reportProgress: true});

    return upload$;
  }

  public formatFileUpload(files, orderId){
    let date = new Date()
    const formData: FormData = new FormData()
    formData.append('file_count', files.length)
    formData.append('order_id', orderId)
    formData.append('created_date', this.helperService.getCurrentDate(date))

    files.map((f, i) => {
      let file: File = f;
      return formData.append('file_'+(i+1), file)
    })
    return formData;
  }

  public downloadPdf(orderId: number, companyId: number, designId: number): Observable<any> {
    return this.http.post(`${this.apiRoot}client-portal/art/download-art-approval/${orderId}/${companyId}/${designId}`, {
      pdf_token: true
    }, { headers: { 'Accept': 'application/pdf' }, responseType: 'blob' }).pipe(
      map((data: any) => {
        this.savePdf(data, orderId, designId);
      })
    );
  }

  private savePdf(data: any, orderId: number, designId: number): void {
    const blob = new Blob([data], { type: 'application/pdf' });
    const url = (window.URL || window.webkitURL).createObjectURL(blob);

    const today = new Date();
    const a: any = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    a.href = url;
    a.download = `ArtApproval_OrderNum${orderId}_DesignNum${designId}_${today.getMonth() + 1}_${today.getDate()}_${today.getFullYear()}`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  public getFeedback(companyId: number, designId: number): Observable<any> {
    return this.apiService.getV2(`api/v2/feedback/${companyId}/${designId}`);
  }

  public createFeedback(payload: any): Observable<any> {
    const { company_id: companyId, design_id: designId } = payload;
    return this.apiService.postV2(`api/v2/feedback/${companyId}/${designId}`, payload).pipe(
      map(response => {
        this.artApprovalEmailResponse('feedback',payload.order_id, companyId, designId, payload).subscribe(
          () => {
            console.log('email sent');
          }, error => {
            console.log('email error', error);
          }
        )
        return response;
      })
    )
  }

  public getImagesForDesign(payload: any): Observable<any> {
    const { companyId, designId } = payload;
    const artApprovals = this.http.get<string[]>(`${environment.apiUrl2}api/v2/cloud/platform/art-approval/${companyId}/${designId}`);
    const photoApprovals = this.http.get<string[]>(`${environment.apiUrl2}api/v2/cloud/platform/photo-approval/${companyId}/${designId}`);
    return forkJoin([artApprovals, photoApprovals]).pipe(
      map(([art, photo]) => [...art, ...photo])
    );
  }

  public artApprovalEmailResponse(type, order_id, company_id, design_id, payload): Observable<any>{
    return this.apiService.post(`art/${type}/${order_id}/${company_id}/${design_id}`,payload, { pdf_token: true });
  }

  /**
   * Presigned S3 URLs for the design-position artwork (`image_1..4`) carried by an
   * order payload (CS-4493). Fetch layer only — no UI consumes this yet.
   *
   * Takes the `designs` array from either order payload and presigns every
   * position image slot in one batched call. Positions are deduped by id first:
   * the approvals response can fan out duplicate position rows via joins (same
   * reason `order-line-items` dedupes), and each duplicate would otherwise cost a
   * redundant HeadObject.
   *
   * Results carry `positionId` + `slot`, so group them with
   * `new Map(...)` keyed on `positionId` to line them up with anything that keys
   * off `order_design_position.id` — which is what the Art Approval page already
   * stores as `PrintPosition.positionId`.
   *
   * ⚠️ NOT a replacement for {@link getImagesForDesign}, which is a LISTING call.
   * `image_1..4` is the single current art file per slot; the art-approval cloud
   * additionally returns proof iterations, photo-approvals, the composed mockup and
   * feedback markups, none of which are named anywhere in the order payload and so
   * cannot be presigned. See docs/art-approval-images-backend-spec.md.
   *
   * ⚠️ Authenticated callers only — the presign endpoint is not in AuthInterceptor's
   * public allowlist, so guest (token-only) views resolve to all-`exists: false`.
   *
   * Fails soft: never errors, and entries with `exists: false` mean "keep whatever
   * image source you are using today".
   */
  public getPositionArtwork(
    companyId: number | string,
    designs: any[],
    expiresIn?: number
  ): Observable<PositionArtwork[]> {
    const positions: any[] = [];
    const seen = new Set<string>();

    for (const design of designs ?? []) {
      // `design_position` on the V2 approvals payload, `positions` on order detail.
      for (const position of (design?.design_position ?? design?.positions ?? [])) {
        if (!position || position.id == null) { continue; }
        const id = String(position.id);
        if (seen.has(id)) { continue; }
        seen.add(id);
        positions.push(position);
      }
    }

    if (!positions.length) { return of([]); }

    return this.storage.presignPositionImages(companyId, positions, expiresIn);
  }

  /**
   * Presigned S3 URL for each design's composed mockup — the Design Mockup card on
   * the Art Approval page.
   *
   * Sibling of {@link getPositionArtwork}: same batched presign call, same fail-soft
   * contract, but keyed on `order_design.design_mokup_image` (a bare filename on the
   * V2 approvals payload) and so on `{companyId}/order_design/{designId}/{filename}`.
   * Designs are deduped by id first, since the approvals response can fan out
   * duplicate rows via joins and each duplicate would cost a redundant HeadObject.
   *
   * Results carry `designId` (`order_design.id`, NOT `display_number`) — that is the
   * join key back onto the page's `DesignGroup.designId`.
   *
   * ⚠️ Authenticated callers only — the presign endpoint is not in AuthInterceptor's
   * public allowlist, so guest (token-only) views resolve to all-`exists: false` and
   * the card simply does not render. Same limitation as the position artwork.
   */
  public getDesignMockups(
    companyId: number | string,
    designs: any[],
    expiresIn?: number
  ): Observable<DesignMockup[]> {
    const unique: any[] = [];
    const seen = new Set<string>();

    for (const design of designs ?? []) {
      if (!design || design.id == null) { continue; }
      const id = String(design.id);
      if (seen.has(id)) { continue; }
      seen.add(id);
      unique.push(design);
    }

    if (!unique.length) { return of([]); }

    return this.storage.presignDesignMockups(companyId, unique, expiresIn);
  }

  /**
   * Excel shipping manifest for an order (BF-65): the same three-step flow the
   * Order Detail download uses — legacy `getShippingOrdersDetail` (type 'box') →
   * `getShippingItemsData` → V2 `exportAllLocationsAndBoxes`, which returns the
   * .xlsx as an ArrayBuffer. Errors (terminating the stream, so callers' take(1)
   * + finalize patterns still release) when the order has no shipping data.
   */
  public getShippingManifestXlsx(orderId: number, companyId: number, clientId: number): Observable<ArrayBuffer> {
    return this.apiService
      .post('shipping/getShippingOrdersDetail', {
        order_id: orderId,
        client_id: clientId,
        type: 'box',
        company_id: companyId
      })
      .pipe(
        switchMap((resp: any) => {
          const shippingData = resp?.data?.shippingData;
          if (!shippingData) {
            return throwError(() => new Error('No shipping data for this order'));
          }
          return this.apiService.post('shipping/getShippingItemsData', {
            shipping_data: shippingData,
            order_id: orderId,
            pdf_token: 'token'
          });
        }),
        switchMap((items: any) => {
          if (!items) {
            return throwError(() => new Error('No shipping items for this order'));
          }
          return this.apiService.postV2(
            'api/v2/pdf/exportAllLocationsAndBoxes',
            { shipping: items },
            { responseType: 'arraybuffer' }
          ) as Observable<ArrayBuffer>;
        })
      );
  }

  /**
   * Print sequence (screensets → ordered ink/thread steps) for a single decoration
   * position. `id` = `order_design_position.id`. Returns an array of screensets, each
   * with an ordered `steps[]` ({ seq, color, threadCode, inkType }). See CS-3428/8.5-b.
   */
  public getPositionPrintSequence(orderDesignPositionId: number | string): Observable<any> {
    return this.http.get(`${this.apiMicroservice}legacy/order-design-position/${orderDesignPositionId}/print-sequence`);
  }
}
